import React from 'react';
import {
  Controller,
  FieldValues,
  UseFormMethods,
  RegisterOptions,
  FieldName,
} from 'react-hook-form';
import parseGooglePlace from 'parse-google-place';
import PlacesAutocomplete, {
  geocodeByPlaceId,
} from 'react-places-autocomplete';
import classNames from 'classnames';
import locationImage from '../../../assets/location.png';
import { TextField } from '../TextField/TextField';
// eslint-disable-next-line import/named
import { FlattenKeys } from '../../../types/utilTypes';
import styles from './styles.module.scss';

interface Props<T extends FieldValues> {
  className?: string;
  label: string;
  tooltip?: string;
  name: FlattenKeys<T>;
  width?: React.ComponentProps<typeof TextField>['width'];
  form: UseFormMethods<T>;
  validation?: RegisterOptions;
  onAddressSelect?: (address: {
    address: string;
    city: string;
    county: string;
    state: string;
    zip: string;
  }) => void;
}

export const Address = <T extends FieldValues>({
  className,
  label,
  tooltip,
  name,
  width,
  form,
  validation,
  onAddressSelect,
}: Props<T>) => {
  const onSelect = async (address: string, placeID: string) => {
    if (!onAddressSelect) return;

    // when a user selects an address, get all the other details such as city/zip/state
    const placeData = await geocodeByPlaceId(placeID);
    if (placeData && placeData.length) {
      const place = parseGooglePlace(placeData[0]);
      onAddressSelect({
        address: place.address,
        city: place.city,
        county: place.county,
        state: place.stateShort,
        zip: place.zipCode,
      });
    }
  };

  return (
    <Controller
      control={form.control}
      name={(name as unknown) as FieldName<T>}
      rules={validation}
      render={({ onChange, onBlur, value }) => (
        <PlacesAutocomplete
          value={value}
          onChange={onChange}
          onSelect={onSelect}
          debounce={200}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps }) => (
            <div className={classNames(styles.container, className)}>
              <TextField
                {...getInputProps({ onBlur })}
                label={label}
                tooltip={tooltip}
                name={name}
                width={width}
                form={form}
                validation={validation}
                skipFormRegistration
              />
              {!!suggestions.length && (
                <div className={styles.autocompleteContainer}>
                  {suggestions.map((suggestion) => {
                    return (
                      <div
                        {...getSuggestionItemProps(suggestion, {
                          className: classNames(
                            styles.suggestionItem,
                            suggestion.active && styles.active,
                          ),
                        })}
                      >
                        <img src={locationImage} alt="Location" />
                        <span>{suggestion.formattedSuggestion.mainText}</span>
                        <span className={styles.secondaryText}>
                          {suggestion.formattedSuggestion.secondaryText}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </PlacesAutocomplete>
      )}
    />
  );
};
